<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { AdminRecord } from '@/types/record'
import type { SelectOption } from '@/types/page'
import { PERMISSION_GROUPS } from '@/data/registry'
import { DATA_SCOPE_OPTIONS } from '@/data/page-defs/common'
import { useAuthStore } from '@/stores/auth'
import { adapter } from '@/services/prototype-adapter'
import { request } from '@/services/request'
import { ApiError } from '@/utils/errors'
import AppIcon from '@/components/AppIcon.vue'
import ProSelect from '@/components/pro/ProSelect.vue'

const props = defineProps<{
  open: boolean
  record: AdminRecord | null
}>()

const emit = defineEmits<{
  close: []
  saved: []
}>()

const auth = useAuthStore()
const saving = ref(false)
const error = ref('')
const keyword = ref('')
const checked = reactive<Record<string, boolean>>({})
const dataScope = ref('business')
const initial = ref('[]')

const canManage = computed(() => auth.has('roles.manage'))
const groups = computed(() => PERMISSION_GROUPS.filter(group => !keyword.value || group.label.includes(keyword.value) || group.module.includes(keyword.value)))
const selectedCount = computed(() => Object.values(checked).filter(Boolean).length)
const dirty = computed(() => JSON.stringify(Object.entries(checked).filter(([, value]) => value).map(([key]) => key).sort()) + dataScope.value !== initial.value)
const memberCount = computed(() => Number(props.record?.member_count ?? 0))
const scopeOptions: SelectOption[] = DATA_SCOPE_OPTIONS

function initialize(): void {
  for (const key of Object.keys(checked)) delete checked[key]
  const codes = Array.isArray(props.record?.permission_codes) ? props.record.permission_codes.map(String) : []
  for (const group of PERMISSION_GROUPS) for (const code of group.permissions) checked[code] = codes.includes(code)
  dataScope.value = String(props.record?.data_scope ?? 'business')
  initial.value = JSON.stringify(Object.entries(checked).filter(([, value]) => value).map(([key]) => key).sort()) + dataScope.value
  error.value = ''
  keyword.value = ''
}

function toggleGroup(permissions: string[], value: boolean): void {
  for (const permission of permissions) checked[permission] = value
}

function groupState(permissions: string[]): { all: boolean; some: boolean } {
  const count = permissions.filter(permission => checked[permission]).length
  return { all: count === permissions.length, some: count > 0 && count < permissions.length }
}

async function save(): Promise<void> {
  if (!props.record || !canManage.value || saving.value) return
  saving.value = true
  error.value = ''
  const permissions = Object.entries(checked).filter(([, value]) => value).map(([key]) => key)
  try {
    await request(
      () => adapter.updateRolePermissions(String(props.record?.id), permissions, dataScope.value, auth.profile?.name ?? '管理员'),
      { write: true, delay: 380, useSimulation: true },
    )
    // Commit the dirty baseline before asking the parent to close. Otherwise
    // the dialog's close watcher would see the stale baseline and show an
    // "unsaved changes" prompt immediately after a successful save.
    initial.value = JSON.stringify(permissions.slice().sort()) + dataScope.value
    ElMessage.success(`角色权限已保存，影响 ${memberCount.value} 名管理员`)
    emit('saved')
    emit('close')
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : '权限保存失败，请重试'
  } finally {
    saving.value = false
  }
}

async function requestClose(): Promise<void> {
  if (!dirty.value) {
    emit('close')
    return
  }
  try {
    await ElMessageBox.confirm('权限变更尚未保存，关闭将放弃这些修改。', '放弃未保存的权限变更', {
      type: 'warning',
      confirmButtonText: '放弃修改',
      cancelButtonText: '继续编辑',
      distinguishCancelAndClose: true,
    })
    emit('close')
  } catch {
    // Keep editing.
  }
}

function onDialogVisible(value: boolean): void {
  if (!value) void requestClose()
}

watch(() => props.open, open => {
  if (open) initialize()
})
</script>

<template>
  <el-dialog :model-value="open" width="760px" align-center :close-on-click-modal="false" class="permission-dialog" @update:model-value="onDialogVisible">
    <template #header>
      <div class="drawer-head">
        <div class="drawer-head__text">
          <div class="drawer-title">权限配置 · {{ record?.name }}</div>
          <div class="drawer-sub">权限取并集，数据范围按最小交集生效；保存后该角色下 {{ memberCount }} 名管理员即时受影响。</div>
        </div>
        <button type="button" class="icon-button" aria-label="关闭权限配置" @click="requestClose"><AppIcon name="close" :size="17" /></button>
      </div>
    </template>

    <div v-if="error" class="info-banner info-banner--danger" style="margin-bottom: 10px" role="alert">{{ error }}</div>

    <div class="permission-toolbar">
      <el-input v-model="keyword" placeholder="搜索模块或权限" clearable style="max-width: 220px" aria-label="搜索权限" />
      <span class="tag tag--info">已选 {{ selectedCount }} 项</span>
      <span class="page-toolbar__spacer" />
      <label class="pro-field__label">数据范围</label>
      <ProSelect v-model="dataScope" :options="scopeOptions" :filterable="false" aria-label="数据范围" style="width: 190px" />
    </div>

    <div class="permission-groups">
      <section v-for="group in groups" :key="group.module" class="permission-group">
        <header class="permission-group__head">
          <label class="checkbox-line">
            <el-checkbox
              :model-value="groupState(group.permissions).all"
              :indeterminate="groupState(group.permissions).some"
              @change="toggleGroup(group.permissions, Boolean($event))"
            />
            <b>{{ group.label }}</b>
          </label>
          <span class="faint">{{ group.permissions.filter(permission => checked[permission]).length }}/{{ group.permissions.length }}</span>
        </header>
        <div class="permission-group__list">
          <label v-for="permission in group.permissions" :key="permission" class="permission-item">
            <el-checkbox v-model="checked[permission]" />
            <span>{{ permission }}</span>
          </label>
        </div>
      </section>
    </div>

    <div v-if="dirty" class="info-banner info-banner--warn" style="margin-top: 10px">权限变更尚未保存。保存前请确认影响范围，历史审计不会因权限调整而删除。</div>

    <template #footer>
      <button type="button" class="btn" :disabled="saving" @click="requestClose">取消</button>
      <button type="button" class="btn btn--primary" :disabled="!canManage || saving || !dirty" @click="save">
        <span v-if="saving" class="button-spinner" aria-hidden="true" />{{ saving ? '保存中…' : '保存权限' }}
      </button>
    </template>
  </el-dialog>
</template>

<style scoped>
.permission-toolbar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding-bottom: 10px; border-bottom: 1px solid var(--c-divider); }
.permission-groups { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; max-height: 52vh; overflow: auto; padding-top: 10px; }
.permission-group { border: 1px solid var(--c-border); border-radius: 9px; overflow: hidden; }
.permission-group__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 8px 10px; background: var(--c-surface-muted); border-bottom: 1px solid var(--c-divider); }
.permission-group__list { display: grid; gap: 2px; padding: 8px 10px; }
.permission-item { display: flex; align-items: center; gap: 6px; min-width: 0; font-size: 10px; color: var(--c-muted); }
.permission-item span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.button-spinner { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }
@media (max-width: 767px) { .permission-groups { grid-template-columns: minmax(0, 1fr); } }
</style>
